import { Typography } from "@mg/dali/src";
import { type WorkosLoginResponse } from "@mg/schemas/src/prince/auth";
import { Box, Button, Flex, Text, Dialog } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";

import { useUI } from "../../contexts/ui";

interface MfaFormProps {
  mfaOptions: WorkosLoginResponse;
  loading: boolean;
  onVerify: (otp: string) => void;
  error?: boolean;
  errorMessage?: string;
  guestLogin?: boolean;
}

export function MfaForm({
  mfaOptions,
  onVerify,
  loading,
  error,
  errorMessage,
  guestLogin = false,
}: MfaFormProps) {
  const [otp, setOtp] = useState("");
  const [secretKeyModalOpen, setSecretKeyModalOpen] = useState(false);
  const { notify } = useUI();
  const { t } = useTranslation("auth");

  const onCopyLink = () => {
    navigator.clipboard.writeText(
      mfaOptions?.authenticationFactor?.totp?.secret as string,
    );
    notify({
      title: t("mfaForm.copiedToClipboard"),
      message: "",
    });
  };

  return (
    <Box mt="4" className="px-4">
      {mfaOptions?.authenticationFactor && (
        <Flex direction="column" gap="4">
          <Dialog.Root
            open={secretKeyModalOpen}
            onOpenChange={setSecretKeyModalOpen}
          >
            <Dialog.Content>
              <div className="mb-4 flex items-center justify-end gap-2">
                <Dialog.Close>
                  <Button color="gray" variant="soft" size="2">
                    x
                  </Button>
                </Dialog.Close>
              </div>
              <Typography>{t("mfaForm.yourSetupKey")}</Typography>
              <Flex align="center" justify="between">
                <Typography weight="bold">
                  {mfaOptions?.authenticationFactor?.totp?.secret}
                </Typography>
                <Button
                  size="2"
                  onClick={onCopyLink}
                  variant="solid"
                  data-testid="mfa-copy-secret-button"
                  aria-label={t("mfaForm.copySetupKey")}
                >
                  {t("mfaForm.copy")}
                </Button>
              </Flex>
            </Dialog.Content>
          </Dialog.Root>

          <Typography as="p" size={guestLogin ? "lg" : "xl"} weight="medium">
            {t("mfaForm.scanTheQrCode")}
          </Typography>
          <Typography size="base">
            {t("mfaForm.useAnAuthenticatorAppLike")}{" "}
            <a
              href="https://1password.com/"
              className="puntt-link"
              data-testid="mfa-authenticator-1password"
            >
              {t("login.mfaForm.1password")}
            </a>
            ,{" "}
            <a
              href="https://apps.apple.com/us/app/google-authenticator/id388497605"
              className="puntt-link"
              data-testid="mfa-authenticator-google"
            >
              {t("login.mfaForm.googleAuthenticator")}
            </a>
            ,{" "}
            <a
              href="https://authy.com/"
              className="puntt-link"
              data-testid="mfa-authenticator-authy"
            >
              {t("login.mfaForm.authy")}
            </a>
            , {t("mfaForm.or")}{" "}
            <a
              href="https://www.microsoft.com/en-gb/security/mobile-authenticator-app"
              className="puntt-link"
              data-testid="mfa-authenticator-microsoft"
            >
              {t("login.mfaForm.microsoftAuthenticator")}
            </a>{" "}
            {t("mfaForm.toScanTheQrCodeBelow")}
          </Typography>
          <Flex
            gap="4"
            align="center"
            justify={guestLogin ? "center" : "start"}
          >
            <img
              src={mfaOptions?.authenticationFactor?.totp?.qrCode}
              width="150"
              height="150"
              alt={t("mfaForm.qrCode")}
              className="mt-4 rounded-sm border-2 border-solid"
            />
            <Box>
              <Typography as="p" size="sm">
                {t("mfaForm.cantScanTheCode")}
              </Typography>
              <Text
                color="blue"
                size="2"
                weight="medium"
                className="cursor-pointer"
                onClick={() => setSecretKeyModalOpen(true)}
                data-testid="mfa-view-setup-key"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setSecretKeyModalOpen(true);
                  }
                }}
              >
                {t("mfaForm.viewSetupKey")}
              </Text>
            </Box>
          </Flex>

          <hr className="mt-8" />
        </Flex>
      )}

      <Flex direction="column" gap="4" className="mt-4">
        <Typography as="p" weight="medium" size="sm">
          {t("mfaForm.enterCodeFromYourApp")}
        </Typography>
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span></span>}
          renderInput={(props) => <input {...props} />}
          inputStyle={guestLogin ? "guest-otp-input-style" : "otp-input-style"}
          inputType="text"
          data-testid="mfa-otp-input"
        />
        <Text
          color="red"
          size="2"
          data-testid="mfa-error-message"
          className={cx({ hidden: !error })}
        >
          {errorMessage}
        </Text>

        <Button
          disabled={otp.length !== 6}
          loading={loading}
          data-testid="form-submit-cta"
          onClick={() => onVerify(otp)}
          size="2"
          className={cx("mt-4", "w-full", "mb-4")}
        >
          {t("mfaForm.verify")}
        </Button>
      </Flex>
    </Box>
  );
}
